import React, { useState } from 'react'
import intl from 'react-intl-universal'
import Logo from '../logo.svg'
import {
  HeaderLeftWrapper,
  HeaderRightWrapper,
  HeaderWrapper,
  LogoWrapper,
  NavBarWrapper,
  NavLink,
  UserActionWrapper,
} from './atoms'
import { NavDropDown } from './nav-dropdown'
import { LocaleMenu } from './locale-menu'
import { MobileNav } from './mobile-nav'
import { SupportDropDown } from './support-dropdown'
import { UserStatus } from './user-status'
import { LOCALE } from '@foxrenderfarm/internals'
import { Link } from 'gatsby'
import { BrowserTip } from './components/browser-tip'

interface Props {
  isMultilingual?: boolean
  currentLocale: LOCALE
  currentPathname: string
  origin: string
  showTopBanner: boolean
  displayLanguages?: LOCALE[]
}

export const Header = ({
  isMultilingual,
  currentLocale,
  currentPathname,
  origin,
  showTopBanner,
  displayLanguages,
}: Props) => {
  const [showMobileNav, setShowMobileNav] = useState(false)

  const navConfig = [
    {
      key: 'home',
      name: intl.get('index.header.home'),
      to: '/',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      key: 'cloud',
      name: intl.get('index.header.cloud'),
      to: '/cloud-rendering.html',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      key: 'pricing',
      name: intl.get('index.header.pricing'),
      to: '/pricing.html',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      key: 'customers',
      name: intl.get('index.header.customers'),
      to: '/client-works.html',
      useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
    },
    {
      key: 'support',
      name: intl.get('index.header.support'),
      children: [
        {
          to: `${origin}/support/`,
          key: 'center',
          name: intl.get('index.header.support.center'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
        {
          to: `${origin}/support/pricing-and-billing/`,
          key: 'pricing-and-billing',
          name: intl.get('index.header.support.pricing.billing'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
        {
          to: `${origin}/support/rendering-tutorial/`,
          key: 'rendering-tutorial',
          name: intl.get('index.header.support.rendering.tutorial'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
        {
          to: `${origin}/support/user-center/`,
          key: 'user',
          name: intl.get('index.header.support.user'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
        {
          to: `${origin}/support/general/`,
          key: 'general',
          name: intl.get('index.header.support.general'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
        {
          to: `${origin}/support/developer-center/`,
          key: 'developer',
          name: intl.get('index.header.support.developer'),
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'support',
        },
      ],
    },
    {
      key: 'about',
      name: intl.get('index.header.about'),
      children: [
        {
          key: 'company',
          name: intl.get('index.header.about.company'),
          to: '/about.html',
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
        },
        {
          key: 'global-resellers',
          name: intl.get('index.header.about.global.resellers'),
          to: '/global-partner.html',
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
        },
        {
          key: 'partners',
          name: intl.get('index.header.about.partners'),
          to: '/partner.html',
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'main',
        },
        {
          key: 'news',
          name: intl.get('index.header.about.news'),
          to: `${origin}${currentLocale === LOCALE.EN ? '' : `/${currentLocale}`}/news/main/`,
          useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'news',
        },
        ...(currentLocale === LOCALE.JP
          ? [
              {
                key: 'jsFaqs',
                name: intl.get('index.header.support.FAQs'),
                to: `${origin}/jp/faq.html`,
                useGatsbyLink: process.env.GATSBY_PACKAGE_NAME === 'legacy-main',
              },
            ]
          : []),
      ],
    },
  ]

  const isActive = (url: string) => {
    return currentLocale === LOCALE.EN
      ? url === currentPathname || `/en${url}` === currentPathname
      : currentPathname === `/${currentLocale}${url}`
  }

  return (
    <HeaderWrapper $showMobileNav={showMobileNav} $showTopBanner={showTopBanner}>
      <HeaderLeftWrapper>
        <LogoWrapper>
          <Link to={currentLocale === LOCALE.EN ? '/' : `/${currentLocale}`}>
            <img src={Logo} alt='Fox Renderfarm Cloud Render Farm Service' loading='lazy' />
          </Link>
        </LogoWrapper>
        <NavBarWrapper>
          {navConfig.map((item) =>
            item.children ? (
              item.key === 'support' ? (
                <SupportDropDown
                  key={item.key}
                  item={item}
                  origin={origin}
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                />
              ) : (
                <NavDropDown
                  key={item.key}
                  item={item}
                  currentLocale={currentLocale}
                  currentPathname={currentPathname}
                />
              )
            ) : item.to ? (
              <NavLink
                key={item.key}
                as={item.useGatsbyLink ? Link : 'a'}
                to={currentLocale === LOCALE.EN ? item.to : `/${currentLocale}${item.to}`}
                href={currentLocale === LOCALE.EN ? item.to : `/${currentLocale}${item.to}`}
                $isActive={isActive(item.to)}
              >
                {item.name}
              </NavLink>
            ) : null,
          )}
        </NavBarWrapper>
      </HeaderLeftWrapper>
      <HeaderRightWrapper>
        <UserActionWrapper>
          <UserStatus locale={currentLocale} />
        </UserActionWrapper>
        {isMultilingual && (
          <LocaleMenu currentLocale={currentLocale} displayLanguages={displayLanguages} />
        )}
        <MobileNav
          currentLocale={currentLocale}
          items={navConfig}
          handleChangeMobileNavShow={(v: boolean) => setShowMobileNav(v)}
        ></MobileNav>
      </HeaderRightWrapper>
      <BrowserTip />
    </HeaderWrapper>
  )
}
